import * as R from 'ramda';

import {
  animatedNavLineFeaturesAtTime,
  prepareRawAnimatedNavlineSections,
  type AnimatedNavLineSection,
} from './animatedSectionBuilder.ts';
import { resolveChevronSideOffsets, shiftLngLat } from './chevronSideOffset.ts';
import { buildChevronRing, buildChevrons, SPACING_M, vectorM } from './navChevrons.ts';
import type { Coordinate, MapFeature, MapFeatureThemer, MapInitialized, MapRendererApp } from './types.ts';
import { createMapFeature, setSource } from './utils.ts';

type WayfindingBadge = {
  canonicalName: string;
  coordinates: Coordinate;
};

type WayfindingSegment = {
  coordinates: Coordinate[];
  segmentType: string;
  ordinalId: string;
  poiId?: string | number;
  badges?: WayfindingBadge[];
  shouldDrawSegment?: boolean;
};

type RenderedNavlineFeature = MapFeature & {
  type: 'Feature';
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon';
    coordinates: Coordinate | Coordinate[] | Coordinate[][];
  };
  properties: {
    ordinalId: string;
    category?: string;
    badge?: string | null;
    chevron?: boolean;
    chevronScale?: number;
    /** True when the chevron is on the floor (ordinal) being viewed; others render faded (see navLayers.json). */
    chevronCurrentFloor?: boolean;
    transitEndpoint?: boolean;
  };
};

type ChevronRun = { coordinates: Coordinate[]; ordinalId: string };

type NavlineState = {
  ordinal: number | null;
  isAnimating: boolean;
  navlineFeatures: RenderedNavlineFeature[];
  rawAnimatedNavLineSections: AnimatedNavLineSection[];
  featureThemer: Promise<MapFeatureThemer>;
};

export default function NavlineController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  fullRoutePreviewEnabled = false,
) {
  const NAV_LINE_SOURCE = 'llNavSource';
  const ANIM_NAV_LINE_SOURCE = 'llAnimatedNavSource';
  const ANIMATION_DURATION = 5000;
  // Chevrons are pre-built at three sizes; one shows per zoom.
  // (see navLayers.json): 1x >= 16.5, medium 14-16.5, large < 14.
  const CHEVRON_MEDIUM_SCALE = 3;
  const CHEVRON_LARGE_SCALE = 12;

  let state: NavlineState = {
    ordinal: null,
    isAnimating: false,
    navlineFeatures: [],
    rawAnimatedNavLineSections: [],
    featureThemer: app.bus.get<undefined, MapFeatureThemer>('map/getMapFeatureThemer'),
  };

  const isCurrentFloor = (ordinalId: string) => ordinalId === `ordinal: ${state.ordinal}`;

  const buildChevronFeatures = (
    themer: MapFeatureThemer,
    runs: ChevronRun[],
    scale: number,
  ): RenderedNavlineFeature[] => {
    const chevrons = runs.flatMap((run, runIndex) =>
      buildChevrons(run.coordinates, scale).map((chevron, index) => ({
        ...chevron,
        run: runIndex,
        index,
        ordinalId: run.ordinalId,
      })),
    );
    // Overlaps are resolved within each floor; chevrons on different floors never push each other.
    const sideOffsetByChevron = new Map(
      [...new Set(chevrons.map(chevron => chevron.ordinalId))].flatMap(ordinalId => {
        const onFloor = chevrons.filter(chevron => chevron.ordinalId === ordinalId);
        const sideOffsets = resolveChevronSideOffsets(onFloor, SPACING_M * scale);
        return onFloor.map((chevron, i) => [chevron, sideOffsets[i]!] as const);
      }),
    );

    const placed = chevrons.flatMap(chevron => {
      const sideOffset = sideOffsetByChevron.get(chevron)!;
      return sideOffset.visible
        ? [
            {
              ...chevron,
              center: shiftLngLat(chevron.center, sideOffset.offset),
              hasSideOffset: sideOffset.offset.some(value => value !== 0),
            },
          ]
        : [];
    });

    return placed.map((chevron, i) => {
      const following = placed[i + 1];
      const next = following?.run === chevron.run ? following : undefined;
      let dir = chevron.dir;
      if (!next || next.hasSideOffset || next.segment !== chevron.segment) {
        const target = next?.center ?? runs[chevron.run]!.coordinates.at(-1)!;
        const [dx, dy] = vectorM(chevron.center, target);
        const length = Math.hypot(dx, dy);
        if (length > 1e-6) {
          const towardTarget: [number, number] = [dx / length, dy / length];
          // Keep turns of 45 degrees or less on the line, allowing for coordinate rounding.
          const aligned = towardTarget[0] * dir[0] + towardTarget[1] * dir[1] >= Math.SQRT1_2 - 1e-9;
          if (!next || next.hasSideOffset || !aligned) {
            dir = towardTarget;
          }
        }
      }
      return themer({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [buildChevronRing(chevron.center, dir, scale)] },
        properties: {
          category: 'nav.primary',
          ordinalId: chevron.ordinalId,
          chevron: true,
          chevronScale: scale,
          chevronCurrentFloor: isCurrentFloor(chevron.ordinalId),
        },
      } as unknown as MapFeature) as RenderedNavlineFeature;
    });
  };

  // Zoom ranges per scale are defined in navLayers.json: medium 14-16.5, large below 14.
  const buildAllChevronFeatures = (themer: MapFeatureThemer, runs: ChevronRun[]): RenderedNavlineFeature[] => [
    ...buildChevronFeatures(themer, runs, 1),
    ...buildChevronFeatures(themer, runs, CHEVRON_MEDIUM_SCALE),
    ...buildChevronFeatures(themer, runs, CHEVRON_LARGE_SCALE),
  ];

  const initMapSources = (sources: Record<string, unknown>) => {
    return {
      ...sources,
      // Higher maxzoom and no simplification keep the small chevron polygons crisp.
      [NAV_LINE_SOURCE]: { ...createMapFeature([]), maxzoom: 24, tolerance: 0 },
      [ANIM_NAV_LINE_SOURCE]: createMapFeature([]),
    };
  };

  app.bus.on<{ segments: WayfindingSegment[]; category?: string }>(
    'map/showNavlineFeatures',
    ({ segments, category = 'primary' }) => {
      mapInitialized().then(async map => {
        const themer = await state.featureThemer;
        const navline = segments.flatMap(toGeoJson).map(feature => themer(feature));

        const newNavlineFeatures =
          category !== 'primary'
            ? navline.map(feature =>
                themer({
                  ...feature,
                  properties: {
                    ...feature.properties,
                    // Preserve transit categories so preview line layers can render every selected leg.
                    category:
                      fullRoutePreviewEnabled &&
                      category === 'multipoint' &&
                      ['nav.transit', 'nav.train'].includes(feature.properties.category ?? '')
                        ? feature.properties.category
                        : `nav.${category}`,
                    badge: null,
                  },
                }),
              )
            : navline;

        // Preview chevrons. Security segments render as primary (design decision).
        const chevronSegments =
          fullRoutePreviewEnabled && ['primary', 'multipoint'].includes(category)
            ? segments.filter(
                segment => segment.shouldDrawSegment && ['nav.primary', 'nav.secure'].includes(segment.segmentType),
              )
            : [];

        // Group drawable segments into continuous runs so chevron spacing stays
        // even across segment boundaries. A run breaks when the next segment does
        // not connect to the current one (coords) or changes floor (ordinal).
        const chevronRuns = chevronSegments.reduce<ChevronRun[]>((runs, segment) => {
          const current = runs[runs.length - 1];
          const first = segment.coordinates[0];
          const last = current?.coordinates[current.coordinates.length - 1];
          const connects = current?.ordinalId === segment.ordinalId && R.equals(last, first);
          if (connects) {
            // Drop the shared join vertex, then append the rest of the segment.
            current.coordinates.push(...segment.coordinates.slice(1));
          } else {
            runs.push({ coordinates: [...segment.coordinates], ordinalId: segment.ordinalId });
          }
          return runs;
        }, []);

        const chevronFeatures = chevronRuns.length ? buildAllChevronFeatures(themer, chevronRuns) : [];
        const transitEndpoints =
          fullRoutePreviewEnabled && ['primary', 'multipoint'].includes(category)
            ? toTransitEndpointsGeoJson(segments).map(feature => themer(feature))
            : [];
        state.navlineFeatures = state.navlineFeatures.concat(newNavlineFeatures, chevronFeatures, transitEndpoints);
        filterFeaturesOnOrdinal(map);

        if (category === 'primary') {
          state.rawAnimatedNavLineSections = prepareRawAnimatedNavlineSections([segments])[0] ?? [];
          if (!fullRoutePreviewEnabled) {
            startAnimation(map);
          }
        }
      });
    },
  );

  const toLineStringGeoJson = (wayfindingSegment: WayfindingSegment): RenderedNavlineFeature => ({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: wayfindingSegment.coordinates,
    },
    properties: {
      category: wayfindingSegment.segmentType,
      ordinalId: wayfindingSegment.ordinalId,
    },
  });

  const toBadgeGeoJson =
    (wayfindingSegment: WayfindingSegment) =>
    (badge: WayfindingBadge): RenderedNavlineFeature => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: badge.coordinates,
      },
      properties: {
        badge: badge.canonicalName,
        ordinalId: wayfindingSegment.ordinalId,
      },
    });

  const toGeoJson = (wayfindingSegment: WayfindingSegment): RenderedNavlineFeature[] => {
    const badges = (wayfindingSegment.badges ?? []).map(toBadgeGeoJson(wayfindingSegment));
    return wayfindingSegment.shouldDrawSegment ? [toLineStringGeoJson(wayfindingSegment), ...badges] : badges;
  };

  const toTransitEndpointsGeoJson = (segments: WayfindingSegment[]): RenderedNavlineFeature[] =>
    segments.flatMap((segment, index) => {
      if (
        !segment.shouldDrawSegment ||
        !['nav.train', 'nav.transit'].includes(segment.segmentType) ||
        segment.coordinates.length < 2
      ) {
        return [];
      }
      const first = segment.coordinates[0]!;
      const last = segment.coordinates.at(-1)!;
      const previous = segments[index - 1];
      const next = segments[index + 1];
      const connects = (adjacent: WayfindingSegment | undefined, point: Coordinate, join: Coordinate | undefined) =>
        adjacent?.shouldDrawSegment &&
        adjacent.coordinates.length > 1 &&
        adjacent.segmentType === segment.segmentType &&
        adjacent.poiId === segment.poiId &&
        R.equals(point, join);
      const endpoints: Coordinate[] = [];
      if (!connects(previous, first, previous?.coordinates.at(-1))) {
        endpoints.push(first);
      }
      if (!connects(next, last, next?.coordinates[0]) && !endpoints.some(point => R.equals(point, last))) {
        endpoints.push(last);
      }
      return endpoints.map((coordinates): RenderedNavlineFeature => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates },
        properties: {
          category: segment.segmentType,
          ordinalId: segment.ordinalId,
          transitEndpoint: true,
        },
      }));
    });

  function reset() {
    mapInitialized().then(map => {
      setSource(NAV_LINE_SOURCE, [], map);
      setSource(ANIM_NAV_LINE_SOURCE, [], map);
      state.navlineFeatures = [];
      state.rawAnimatedNavLineSections = [];
      state.isAnimating = false;
    });
  }

  app.bus.on('map/resetNavlineFeatures', reset);
  app.bus.on('map/cleanMap', reset);

  const startAnimation = async (map: Awaited<ReturnType<MapInitialized>>) => {
    const themer = await state.featureThemer;
    state.isAnimating = true;
    animate(Date.now(), Date.now(), map, themer);
    setTimeout(() => {
      setSource(ANIM_NAV_LINE_SOURCE, [], map);
      state.isAnimating = false;
    }, ANIMATION_DURATION);
  };

  const animate = async (
    startTime: number,
    nowTime: number,
    map: Awaited<ReturnType<MapInitialized>>,
    themer: MapFeatureThemer,
  ) => {
    if (!state.isAnimating || !R.length(state.rawAnimatedNavLineSections)) {
      return;
    }

    const timeoffset = nowTime - startTime;
    const allAnimatedNavlineFeatures = animatedNavLineFeaturesAtTime(timeoffset, state.rawAnimatedNavLineSections);
    const animatedNavlineFeatures = allAnimatedNavlineFeatures
      .filter(feature => feature.properties.ordinal === state.ordinal)
      .map(feature => themer(feature));

    setSource(ANIM_NAV_LINE_SOURCE, animatedNavlineFeatures, map);
    requestAnimationFrame(() => animate(startTime, Date.now(), map, themer));
  };

  app.bus.on<{ ordinal: number | null }>('map/ordinalChanged', ({ ordinal }) =>
    mapInitialized().then(map => {
      state.ordinal = ordinal;
      // Chevron positions don't depend on the viewed floor; only the current-floor styling changes.
      state.navlineFeatures = state.navlineFeatures.map(feature =>
        feature.properties.chevron
          ? {
              ...feature,
              properties: { ...feature.properties, chevronCurrentFloor: isCurrentFloor(feature.properties.ordinalId) },
            }
          : feature,
      );
      filterFeaturesOnOrdinal(map);
    }),
  );

  const filterFeaturesOnOrdinal = (map: Awaited<ReturnType<MapInitialized>>) => {
    // Preview shows all floors; legacy filters to the active floor.
    const navlineFeaturesOnOrdinal = fullRoutePreviewEnabled
      ? state.navlineFeatures
      : state.navlineFeatures.filter(feature => feature.properties.ordinalId === `ordinal: ${state.ordinal}`);
    setSource(NAV_LINE_SOURCE, navlineFeaturesOnOrdinal, map);
  };

  const runTest = async (initialState: Partial<NavlineState>, testRoutine: () => Promise<void> | void) => {
    state = R.mergeRight(state, initialState) as NavlineState;
    await testRoutine();
    return state;
  };

  return { initMapSources, runTest };
}
